import { useCallback, useContext, useMemo } from 'react'
import { useTranslate } from 'react-polyglot'
import {
  BroadcastOutlinedIcon,
  InboxFilledIcon,
  InboxOutlinedIcon,
  MessageDotsFilledIcon,
  MessageDotsOutlinedIcon,
  SettingsFilledIcon,
  SettingsOutlinedIcon,
} from '@getgo/chameleon-icons/react'
import { Permissions } from '@ucc/admin-global-state'
import { SETTINGS_ROUTE } from '@ucc/unified-admin-common'

import { ContactCenterUserContext, OrganizationContext, UnleashContext } from 'components'
import { SidebarLink } from 'types/SidebarLink'
import { Privileges } from 'utils/rolesToPrivilegesMapper'

type InboxLinksKeys =
  | 'ChatAssistantsInboxLink'
  | 'InboxSettingsInboxLink'
  | 'EngageSettingsInboxLink'
  | 'CustomerEngagementSettingsLink'

export type UseInboxLinksProps = {
  SkeletonLink: { key: string }
}

export type UseInboxLinks = {
  inboxRoute: ({ key: string } | SidebarLink)[]
  inboxLinks: { [K in InboxLinksKeys]: SidebarLink }
  filterInbox: () => string[]
}

export function useInboxLinks({ SkeletonLink }: UseInboxLinksProps): UseInboxLinks {
  const t = useTranslate()
  const { userContext } = useContext(ContactCenterUserContext)
  const pbx = useContext(OrganizationContext)
  const { inboxSettingsIntegrationEnabled } = useContext(UnleashContext)

  const inboxLinks = useMemo<{ [K in InboxLinksKeys]: SidebarLink }>(
    () => ({
      ChatAssistantsInboxLink: {
        htmlId: 'inbox-chat-assistants',
        key: 'inbox-chat-assistants',
        to: `/inbox/chat-assistants`,
        primary: t('Chat Assistants'),
        activeIcon: <MessageDotsFilledIcon />,
        inactiveIcon: <MessageDotsOutlinedIcon />,
        privilege: Privileges.CallQueueLinks,
        globalSearchBarDetails: [
          {
            keyword: t('Chat Assistants'),
            to: `/inbox/chat-assistants`,
          },
        ],
      },
      InboxSettingsInboxLink: {
        key: 'inbox-settings',
        to: '/inbox/inboxes',
        primary: t('Inboxes'),
        activeIcon: <InboxFilledIcon />,
        inactiveIcon: <InboxOutlinedIcon />,
        privilege: Privileges.AppLinks,
      },
      EngageSettingsInboxLink: {
        key: 'customer-engagement-settings',
        to: `/inbox/customer-engagement`,
        primary: t('Engage settings'),
        activeIcon: <SettingsFilledIcon />,
        inactiveIcon: <SettingsOutlinedIcon />,
        privilege: Privileges.AppLinks,
        globalSearchBarDetails: [
          {
            keyword: t('Engage settings'),
            to: `/inbox/customer-engagement`,
            tags: [t('Digital'), t('SMS marketing'), t('Campaigns'), t('Message assistant'), t('Customer engagement')],
          },
        ],
      },
      CustomerEngagementSettingsLink: {
        key: 'customer-engagement-settings',
        to: `/${SETTINGS_ROUTE}/customer-engagement`,
        primary: t('Engage'),
        activeIcon: <BroadcastOutlinedIcon />,
        inactiveIcon: <BroadcastOutlinedIcon />,
        privilege: Privileges.AppLinks,
        globalSearchBarDetails: [
          {
            keyword: t('Customer Engagement settings'),
            to: `/${SETTINGS_ROUTE}/customer-engagement`,
            tags: [t('Digital'), t('SMS marketing'), t('Campaigns'), t('Message assistant'), t('Customer engagement')],
          },
        ],
      },
    }),
    [t],
  )

  const filterInbox = useCallback<() => string[]>(
    () => [
      ...(userContext?.permissions?.includes(Permissions.ENGAGEMENTS_SHARED_INBOX) && pbx?.showCustomerEngagement
        ? []
        : [inboxLinks.InboxSettingsInboxLink.key!]),
      ...(userContext?.permissions?.includes(Permissions.ENGAGEMENTS_SETTINGS) && pbx?.showCustomerEngagement
        ? []
        : [inboxLinks.EngageSettingsInboxLink.key!]),
      ...(userContext?.permissions?.includes(Permissions.ENGAGEMENTS_SETTINGS_GENERAL) && pbx?.showCustomerEngagement
        ? []
        : [inboxLinks.CustomerEngagementSettingsLink.key!]),
      ...(userContext?.permissions?.includes(Permissions.SMART_RESPONSES_VIEW)
        ? []
        : [inboxLinks.ChatAssistantsInboxLink.key!]),
    ],
    [inboxLinks, pbx?.showCustomerEngagement, userContext?.permissions],
  )

  const inboxRoute = useMemo<({ key: string } | SidebarLink)[]>(
    () => [
      pbx?.showCustomerEngagement !== undefined &&
      userContext?.permissions?.includes(Permissions.ENGAGEMENTS_SHARED_INBOX) &&
      inboxSettingsIntegrationEnabled
        ? inboxLinks.InboxSettingsInboxLink
        : SkeletonLink,
      pbx?.showCustomerEngagement !== undefined &&
      userContext?.permissions?.includes(Permissions.ENGAGEMENTS_SETTINGS) &&
      inboxSettingsIntegrationEnabled
        ? inboxLinks.EngageSettingsInboxLink
        : SkeletonLink,
      userContext?.permissions?.includes(Permissions.SMART_RESPONSES_VIEW) && inboxSettingsIntegrationEnabled
        ? inboxLinks.ChatAssistantsInboxLink
        : SkeletonLink,
    ],
    [SkeletonLink, inboxLinks, inboxSettingsIntegrationEnabled, pbx?.showCustomerEngagement, userContext?.permissions],
  )

  return useMemo<UseInboxLinks>(
    () => ({
      inboxRoute,
      inboxLinks,
      filterInbox,
    }),
    [inboxRoute, inboxLinks, filterInbox],
  )
}
